/**
 * In-app confirmation dialog — replaces native confirm() everywhere.
 *
 * Two ways to use it:
 *
 * 1. Declarative (no page JS): put data-confirm="message" on a <form> or on a
 *    submit <button>. Submission is intercepted, the dialog shown, and the
 *    form re-submitted only when the user confirms. Optional attributes:
 *      data-confirm-title   dialog title
 *      data-confirm-ok      confirm button label
 *      data-confirm-danger  present → red confirm button
 *
 * 2. Programmatic: await appConfirm({title, message, detail, okLabel,
 *    cancelLabel, danger, signal}) → Promise<boolean>. An AbortSignal closes
 *    the dialog from outside (the question stopped mattering — e.g. the save
 *    it asked about finished) and resolves false, like a cancel.
 *
 * Design choices for destructive actions: the CANCEL button takes initial
 * focus so a stray Enter never confirms; Escape and an overlay click cancel.
 * Reuses the existing .modal-overlay/.modal component (needs the .show class
 * to become visible).
 */

const LABELS = {
    ko: { ok: '확인', cancel: '취소', title: '확인이 필요합니다' },
    en: { ok: 'Confirm', cancel: 'Cancel', title: 'Please confirm' },
};

// Korean text breaks between words, never inside one (HRGA 다듬기 Task 12 — the X-6 rule the HRGA screens give
// their own text with .hrga-text): the dialog lives outside every screen root, so its body wrapped 「이 달의」 as
// 「이 달/의」 and 「개편안」 as 「개편/안」. overflow-wrap still breaks a word that would not fit the dialog at all.
function keepWords(el) {
    el.style.wordBreak = 'keep-all';
    el.style.overflowWrap = 'anywhere';
}

function labels() {
    const lang = (document.documentElement.lang || 'ko').toLowerCase();
    return LABELS[lang.startsWith('en') ? 'en' : 'ko'];
}

export function appConfirm(opts) {
    opts = opts || {};
    const L = labels();

    return new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.className = 'modal-overlay confirm-dialog-overlay';
        // Above stacked modals so a confirm launched from inside one is visible.
        overlay.style.zIndex = 'calc(var(--z-modal) + 20)';

        const modal = document.createElement('div');
        modal.className = 'modal';
        modal.setAttribute('role', 'alertdialog');
        modal.setAttribute('aria-modal', 'true');
        modal.style.maxWidth = '26rem';

        const title = document.createElement('h3');
        title.className = 'modal-title';
        title.textContent = opts.title || L.title;

        const header = document.createElement('div');
        header.className = 'modal-header';
        header.appendChild(title);

        const body = document.createElement('div');
        body.className = 'modal-body';
        keepWords(body);
        const message = document.createElement('p');
        message.style.margin = '0';
        message.textContent = opts.message || '';
        body.appendChild(message);
        if (opts.detail) {
            const detail = document.createElement('p');
            detail.className = 'text-muted';
            detail.style.cssText = 'margin: var(--space-3) 0 0; font-size: var(--text-sm);';
            detail.textContent = opts.detail;
            body.appendChild(detail);
        }

        const cancelBtn = document.createElement('button');
        cancelBtn.type = 'button';
        cancelBtn.className = 'btn btn-secondary';
        cancelBtn.textContent = opts.cancelLabel || L.cancel;

        const okBtn = document.createElement('button');
        okBtn.type = 'button';
        okBtn.className = opts.danger ? 'btn btn-error' : 'btn btn-primary';
        okBtn.textContent = opts.okLabel || L.ok;

        const footer = document.createElement('div');
        footer.className = 'modal-footer';
        footer.style.cssText = 'display: flex; justify-content: flex-end; gap: var(--space-3); padding: var(--space-4) var(--space-6); border-top: 1px solid var(--gray-200);';
        footer.appendChild(cancelBtn);
        footer.appendChild(okBtn);

        modal.appendChild(header);
        modal.appendChild(body);
        modal.appendChild(footer);
        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        let settled = false;
        const signal = opts.signal;
        const onAbort = () => finish(false);
        function finish(result) {
            if (settled) return;
            settled = true;
            overlay.classList.remove('show');
            document.removeEventListener('keydown', onKey, true);
            if (signal) signal.removeEventListener('abort', onAbort);
            // Let the closing transition play before dropping the node.
            setTimeout(() => overlay.remove(), 200);
            resolve(result);
        }

        function onKey(e) {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                finish(false);
            }
        }

        cancelBtn.addEventListener('click', () => finish(false));
        okBtn.addEventListener('click', () => finish(true));
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) finish(false);
        });
        document.addEventListener('keydown', onKey, true);
        if (signal) {
            if (signal.aborted) { finish(false); return; }
            signal.addEventListener('abort', onAbort);
        }

        // .show on the next frame so the opening transition runs.
        requestAnimationFrame(() => {
            if (settled) return;
            overlay.classList.add('show');
            cancelBtn.focus(); // a stray Enter must never confirm a destructive action
        });
    });
}

/**
 * appChoose({ title, message, choices: [{ value, label, danger, primary }], cancelLabel })
 * → Promise<value | null>. Same look as appConfirm with one button per choice
 * (반복 일정의 「이 일정만 / 이후 모두 / 전체」). Esc·바깥 클릭·취소 = null.
 * The first choice gets focus; ←/→ move between the buttons.
 */
export function appChoose(opts) {
    opts = opts || {};
    const L = labels();
    const choices = Array.isArray(opts.choices) ? opts.choices : [];
    return new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.className = 'modal-overlay confirm-dialog-overlay';
        overlay.style.zIndex = 'calc(var(--z-modal) + 20)';
        const modal = document.createElement('div');
        modal.className = 'modal';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.style.maxWidth = '30rem';
        const title = document.createElement('h3');
        title.className = 'modal-title';
        title.textContent = opts.title || L.title;
        const header = document.createElement('div');
        header.className = 'modal-header';
        header.appendChild(title);
        const body = document.createElement('div');
        body.className = 'modal-body';
        keepWords(body);
        if (opts.message) {
            const message = document.createElement('p');
            message.style.margin = '0';
            message.textContent = opts.message;
            body.appendChild(message);
        }
        const footer = document.createElement('div');
        footer.className = 'modal-footer';
        footer.style.cssText = 'display: flex; justify-content: flex-end; gap: var(--space-2); flex-wrap: wrap; padding: var(--space-4) var(--space-6); border-top: 1px solid var(--gray-200);';
        const cancelBtn = document.createElement('button');
        cancelBtn.type = 'button';
        cancelBtn.className = 'btn btn-secondary';
        cancelBtn.textContent = opts.cancelLabel || L.cancel;
        footer.appendChild(cancelBtn);
        const buttons = [];
        choices.forEach((ch) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = ch.danger ? 'btn btn-error' : (ch.primary ? 'btn btn-primary' : 'btn btn-secondary');
            b.textContent = ch.label;
            b.dataset.choice = ch.value;
            footer.appendChild(b);
            buttons.push(b);
        });
        modal.appendChild(header);
        if (opts.message) modal.appendChild(body); // 본문이 없으면 빈 띠를 그리지 않는다
        modal.appendChild(footer);
        overlay.appendChild(modal);
        document.body.appendChild(overlay);
        let settled = false;
        function finish(result) {
            if (settled) return;
            settled = true;
            overlay.classList.remove('show');
            document.removeEventListener('keydown', onKey, true);
            setTimeout(() => overlay.remove(), 200);
            resolve(result);
        }
        function onKey(e) {
            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); finish(null); return; }
            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                const all = [cancelBtn].concat(buttons);
                const i = all.indexOf(document.activeElement);
                if (i < 0) return;
                e.preventDefault();
                all[(i + (e.key === 'ArrowRight' ? 1 : all.length - 1)) % all.length].focus();
            }
        }
        cancelBtn.addEventListener('click', () => finish(null));
        buttons.forEach((b) => b.addEventListener('click', () => finish(b.dataset.choice)));
        overlay.addEventListener('click', (e) => { if (e.target === overlay) finish(null); });
        document.addEventListener('keydown', onKey, true);
        requestAnimationFrame(() => {
            overlay.classList.add('show');
            (buttons[0] || cancelBtn).focus();
        });
    });
}

/**
 * Intercepts submission of any form carrying data-confirm — on the form
 * itself or on the submit button that triggered it. Runs in the capture
 * phase so it fires before inline onsubmit handlers.
 */
function interceptSubmits() {
    document.addEventListener('submit', (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form.dataset.confirmed === 'true') {
            delete form.dataset.confirmed;
            return; // our own re-submission, let it through
        }

        const source = form.dataset.confirm != null ? form
            : (e.submitter && e.submitter.dataset.confirm != null ? e.submitter : null);
        if (!source) return;

        e.preventDefault();
        e.stopImmediatePropagation();
        const submitter = e.submitter;

        appConfirm({
            title: source.dataset.confirmTitle,
            message: source.dataset.confirm,
            okLabel: source.dataset.confirmOk,
            danger: source.dataset.confirmDanger != null,
        }).then((ok) => {
            if (!ok) return;
            form.dataset.confirmed = 'true';
            // requestSubmit preserves the submitter (its name/value and any
            // formaction), unlike submit().
            if (submitter && form.requestSubmit) form.requestSubmit(submitter);
            else if (form.requestSubmit) form.requestSubmit();
            else form.submit();
        });
    }, true);
}

export function initConfirmDialogs() {
    interceptSubmits();
    // Global for template inline handlers that build forms dynamically.
    window.appConfirm = appConfirm;
    window.appChoose = appChoose;
}
